import {
    animate,
    animateChild,
    group,
    query,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { Platform } from '@angular/cdk/platform';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    OnInit,
} from '@angular/core';
import { APP_TOAST_DURATION, APP_TOAST_MAX_ITEMS } from '@constants/app.constant';
import { Toast, ToastPosition } from '@models/common.model';
import { ToastService } from '@services/toast.service';
import { distinctUntilChanged } from 'rxjs';

@Component({
    selector: 'pn-toast',
    template: `
        <div *ngIf="!_isServer" class="pn-toast-container" role="alert" [ngClass]="position()">
            <div class="pn-toast-list">
                @for (item of toasts.toReversed(); track item.id) {
                    <pn-toast-item
                        @showToast
                        class="mt-3"
                        [ngClass]="{
                            'mt-3': !$first,
                        }"
                        [toast]="item"
                        [index]="$index"
                        (handleDismiss)="handleDismiss($event)"
                    />
                }
            </div>
        </div>
    `,
    styleUrl: './toast.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('showToast', [
            transition(':enter', [
                style({ height: 0, marginTop: 0 }),
                group([
                    animate(
                        '500ms cubic-bezier(0.35, 0, 0.25, 1)',
                        style({ height: '*', marginTop: '*' })
                    ),
                    query('@*', animateChild()),
                ]),
            ]),
            transition(':leave', [
                animate(
                    '300ms',
                    style({ height: 0, opacity: 0, transform: 'scale(0.9)', marginTop: 0 })
                ),
            ]),
        ]),
    ],
})
export class ToastComponent implements OnInit {
    public position = input<ToastPosition>('top-right');

    public toasts: Toast[] = [];
    protected _isServer = !inject(Platform).isBrowser;

    constructor(
        private readonly _cd: ChangeDetectorRef,
        private readonly _toastService: ToastService
    ) {}

    ngOnInit(): void {
        if (this._isServer) {
            return;
        }
        this._toastService.toasts.pipe(distinctUntilChanged()).subscribe((toast) => {
            if (Array.isArray(toast)) {
                this.toasts = [...this.toasts, ...toast];
            } else {
                const index = this.toasts.findIndex((t) => t.id === toast.id);
                if (index > -1) {
                    const existingToast = this.toasts[index];
                    const newDuration = toast.duration || APP_TOAST_DURATION;
                    this.toasts[index] = {
                        ...toast,
                        duration: (existingToast.duration || APP_TOAST_DURATION) + newDuration,
                    };
                } else {
                    this.toasts = [...this.toasts, toast];
                }
            }

            if (this.toasts.length > APP_TOAST_MAX_ITEMS) {
                this.toasts = this.toasts.slice(this.toasts.length - APP_TOAST_MAX_ITEMS);
            }
            this._cd.markForCheck();
        });

        this._toastService.clearObserver.subscribe((toastId) => {
            if (!toastId) {
                this.toasts = [];
                this._cd.detectChanges();
                return;
            }

            const index = this.toasts.findIndex((toast) => toast.id === toastId);
            if (index > -1) {
                this.toasts.splice(index, 1);
                this._cd.detectChanges();
            }
        });
    }

    /**
     * Handle dismiss
     *
     * @param {{ index: number; toast: Toast }} event
     * @memberof ToastComponent
     */
    public handleDismiss(event: { index: number; toast: Toast }) {
        this.toasts = this.toasts.filter((t) => t.id !== event.toast.id);
        this._cd.detectChanges();
    }
}
